import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { enableTestConfig, resetTestConfig, setTestConfig } from "../../../actions/elasticSearchActions";
import esProxyClient from "../../../api/elasticSearchAPI";
import { initialState } from "../../../reducers/elasticSearch"; // Import initialState
import classes from "./ElasticSearchConfigButton.module.css";

const ElasticSearchConfigButton = () => {
    const dispatch = useDispatch();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [defaultTestConfig, setDefaultTestConfig] = useState(null);

    const testConfig = useSelector((state) => state.elasticSearch.testConfig);
    const testConfigEnabled = useSelector((state) => state.elasticSearch.testConfigEnabled);

    // Fetch default search fields when component mounts
    useEffect(() => {
        const fetchDefaultFields = async () => {
            try {
                const defTestConfig = await esProxyClient.getDefaultTestConfig();
                setDefaultTestConfig(defTestConfig);
            } catch (error) {
                console.error("Failed to fetch default test config:", error);
                // Fallback to initial state if fetch fails
                setDefaultTestConfig(initialState.testConfig);
            }
        };
        fetchDefaultFields();
    }, []);

    // Helper function to derive the initial form state
    const getInitialFormState = useCallback(() => {
        if (defaultTestConfig) {
            return defaultTestConfig;
        }
        const defaultFields = initialState.testConfig.searchFields;
        return {
            explain: testConfig?.explain ?? initialState.testConfig.explain,
            searchFields: testConfig?.searchFields?.length > 0 ? testConfig.searchFields : defaultFields,
            searchTermBoost: testConfig?.searchTermBoost ?? initialState.testConfig.searchTermBoost,
            minScore: testConfig?.minScore ?? initialState.testConfig.minScore,
            distanceScore: {
                decayFunctionType: testConfig?.distanceScore?.decayFunctionType ?? initialState.testConfig.distanceScore.decayFunctionType,
                scale: testConfig?.distanceScore?.scale ?? initialState.testConfig.distanceScore.scale,
                offset: testConfig?.distanceScore?.offset ?? initialState.testConfig.distanceScore.offset,
                decay: testConfig?.distanceScore?.decay ?? initialState.testConfig.distanceScore.decay,
                boost: testConfig?.distanceScore?.boost ?? initialState.testConfig.distanceScore.boost,
            },
        };
    }, [defaultTestConfig, testConfig]);

    const [formState, setFormState] = useState(() => getInitialFormState());

    // Update form state when default search fields are loaded
    useEffect(() => {
        setFormState(getInitialFormState());
    }, [getInitialFormState]);

    const handleSaveConfig = () => {
        dispatch(setTestConfig(formState));
        setIsDialogOpen(false);
    };

    const handleResetConfig = () => {
        setFormState(getInitialFormState()); // Reset local state
        dispatch(resetTestConfig()); // Reset Redux state
    };

    const handleResetSearchFields = () => {
        const testConfig = defaultTestConfig || initialState.testConfig.searchFields;
        setFormState({
            ...formState,
            ...testConfig
        });
    };

    const updateSearchField = (index, value) => {
        const updatedFields = [...formState.searchFields];
        updatedFields[index] = value;
        setFormState({ ...formState, searchFields: updatedFields });
    };

    const handleAddField = () => {
        setFormState({
            ...formState,
            searchFields: [...formState.searchFields, ""],
        });
    };

    const handleRemoveField = (index) => {
        const updatedFields = formState.searchFields.filter((_, i) => i !== index);
        setFormState({ ...formState, searchFields: updatedFields });
    };

    const handleTopLevelInputChange = (field, value) => {
        setFormState({
            ...formState,
            [field]: value,
        });
    };
    const handleDistanceScoreInputChange = (field, value) => {
        setFormState({
            ...formState,
            distanceScore: {
                ...formState.distanceScore,
                [field]: value,
            },
        });
    };

    const onClose = () => {
        handleSaveConfig();
    };

    const copyFieldsToClipboard = () => {
        let fieldsText = '';
        let lastBoost = null;

        formState.searchFields.forEach((field) => {
            // Extract boost value from field (e.g., 'name^4' -> 4, 'name' -> 1)
            const boostMatch = field.match(/\^([\d.]+)$/);
            const currentBoost = boostMatch ? parseFloat(boostMatch[1]) : 1;

            // Add blank line if boost value changed (except for first field)
            if (lastBoost !== null && currentBoost !== lastBoost) {
                fieldsText += '\n';
            }

            fieldsText += `      '${field}',\n`;
            lastBoost = currentBoost;
        });

        navigator.clipboard.writeText(fieldsText.trim());
    };

    return (
        <>
            <button
                className={classes.cogButton}
                onClick={() => setIsDialogOpen(true)}
            >
                ⚙️
            </button>
            {isDialogOpen && (
                <Modal
                    className={classes.esModal}
                    show={isDialogOpen}
                    onHide={onClose}
                >
                    <Modal.Header closeButton>
                        <Modal.Title className={classes.modalHeader}>
                            Configure Test Config
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <label className={classes.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={testConfigEnabled}
                                onChange={(e) => dispatch(enableTestConfig(e.target.checked))}
                                className={classes.checkboxInput}
                            />
                            Enable Test Config
                            <HelpButton
                                tooltip="Check this box to enable the test config. 
                  If this box is unchecked - this entire dialog is ignored and the server decides on parameters."
                            />
                        </label>
                        <br />
                        <form
                            id="testConfigForm"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSaveConfig();
                            }}
                        >
                            <fieldset disabled={!testConfigEnabled}> {/* Wrap form elements in a fieldset to disable them */}
                                <label className={classes.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        checked={formState.explain}
                                        onChange={(e) => handleTopLevelInputChange("explain", e.target.checked)}
                                        className={classes.checkboxInput}
                                    />
                                    Enable Explain
                                    <HelpButton
                                        tooltip="Check this box to enable the explain feature. 
                  If this box is checked - after each result, a call will be made to ElasticSearch's /explain endpoint for each retrieved document.
                  Use this to understand how the score was calculated for each result."
                                    />
                                </label>
                                <div className={classes.formLabel}>
                                    <h4>
                                        Min Score:
                                        <HelpButton
                                            tooltip="Minimum score threshold. Documents with scores below this value will be excluded from the results. Use 0 to include all results."
                                            docUrl="https://www.elastic.co/guide/en/elasticsearch/reference/current/search-request-body.html#request-body-search-min-score"
                                        />
                                        <input
                                            type="number"
                                            name="minScore"
                                            value={formState.minScore}
                                            onChange={(e) => handleTopLevelInputChange("minScore", parseFloat(e.target.value) || 0)}
                                            className={classes.searchFieldInput}
                                            step="0.1"
                                            min="0"
                                        />
                                    </h4>
                                </div>
                                <div className={classes.formLabel}>
                                    <h2>Distance Score</h2>
                                    <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        Decay Function Type:
                                        <HelpButton
                                            tooltip="Choose the decay function type for scoring: linear, exp, or gauss. See illustration in link."
                                            docUrl="https://www.elastic.co/docs/reference/query-languages/query-dsl/query-dsl-function-score-query#function-decay"
                                        />
                                        <div className={classes.radioGroup}>
                                            {["linear", "exp", "gauss"].map((value) => (
                                                <label key={value} className={classes.radioLabel} style={{ marginRight: 8 }}>
                                                    <input
                                                        type="radio"
                                                        name="decayFunctionType"
                                                        value={value}
                                                        checked={
                                                            formState.distanceScore.decayFunctionType === value
                                                        }
                                                        onChange={(e) =>
                                                            handleDistanceScoreInputChange(
                                                                "decayFunctionType",
                                                                e.target.value
                                                            )
                                                        }
                                                        className={classes.radioInput}
                                                    />
                                                    {value}
                                                </label>
                                            ))}
                                        </div>
                                    </h4>
                                    <h4>
                                        Scale:
                                        <HelpButton
                                            tooltip="Set the scale parameter for the decay function (e.g., 10km, 100m). Bigger scale means score decays more gradually, see illustration in link."
                                            docUrl="https://www.elastic.co/docs/reference/query-languages/query-dsl/query-dsl-function-score-query#function-decay"
                                        />
                                        <input
                                            type="text"
                                            name="scale"
                                            value={formState.distanceScore.scale}
                                            onChange={(e) => handleDistanceScoreInputChange("scale", e.target.value)}
                                            className={classes.searchFieldInput}
                                        />
                                    </h4>
                                    <h4>
                                        Offset:
                                        <HelpButton
                                            tooltip="Offset means 'from what distance should we even calculate score' (e.g., 2km, 100m). Bellow this offset, score will be 1 (max). See illustration in link."
                                            docUrl="https://www.elastic.co/docs/reference/query-languages/query-dsl/query-dsl-function-score-query#function-decay"
                                        />
                                        <input
                                            type="text"
                                            name="offset"
                                            value={formState.distanceScore.offset}
                                            onChange={(e) =>
                                                handleDistanceScoreInputChange("offset", e.target.value)
                                            }
                                            className={classes.searchFieldInput}
                                        />
                                    </h4>
                                    <h4>
                                        Decay:
                                        <HelpButton
                                            tooltip="decay means 'the score at distance offset+scale', default is 0.5. See illustration in link."
                                            docUrl="https://www.elastic.co/docs/reference/query-languages/query-dsl/query-dsl-function-score-query#function-decay"
                                        />
                                        <input
                                            type="number"
                                            name="decay"
                                            value={formState.distanceScore.decay}
                                            onChange={(e) => handleDistanceScoreInputChange("decay", parseFloat(e.target.value))}
                                            className={classes.searchFieldInput}
                                        />
                                    </h4>
                                    <h4>
                                        boost:
                                        <HelpButton
                                            tooltip="by how much to boost (multiply) the distance score. default is 1 (no boost). ElasticSearch calls this 'weight'."
                                            docUrl="https://www.elastic.co/docs/reference/query-languages/query-dsl/query-dsl-function-score-query#function-weight"
                                        />

                                        <input
                                            type="number"
                                            name="boost"
                                            value={formState.distanceScore.boost}
                                            onChange={(e) => handleDistanceScoreInputChange("boost", parseFloat(e.target.value))}
                                            className={classes.searchFieldInput}
                                        />
                                    </h4>
                                </div>

                                <div>
                                    <h2>
                                        Search Term
                                    </h2>
                                    <h4>
                                        boost
                                        <HelpButton
                                            tooltip="by how much to boost (multiply) the search-term score. default is 1 (no boost)."
                                            docUrl="https://www.elastic.co/docs/reference/query-languages/query-dsl/query-dsl-match-query"
                                        />:
                                        <input
                                            type="number"
                                            name="searchTermBoost"
                                            value={formState.searchTermBoost}
                                            onChange={(e) => handleTopLevelInputChange("searchTermBoost", parseFloat(e.target.value))}
                                            className={classes.searchFieldInput}
                                        />
                                    </h4>
                                    <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        Fields
                                        <HelpButton
                                            tooltip="Specify which fields ElasticSearch should search in. Add '^3' to the field name to boost it by 3 (e.g., 'name^3'). In the link you can see the availble field names, and to which entities they refer."
                                            docUrl="https://test-q.vegan-friendly.com/textFields"
                                        />
                                        <button
                                            type="button"
                                            onClick={copyFieldsToClipboard}
                                            className={classes.copyButton}
                                            title="Copy fields in code format"
                                            style={{
                                                background: "none",
                                                border: "1px solid #007bff",
                                                color: "#007bff",
                                                cursor: "pointer",
                                                padding: "2px 6px",
                                                borderRadius: "3px",
                                                fontSize: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}
                                        >📋</button>
                                    </h4>
                                    {formState.searchFields.map((field, index) => (
                                        <div key={index} className={classes.searchFieldRow}>
                                            <input
                                                id={"searchFieldInput-" + index}
                                                type="text"
                                                value={field}
                                                onChange={(e) =>
                                                    updateSearchField(index, e.target.value)
                                                }
                                                className={classes.searchFieldInput}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveField(index)}
                                                className={classes.removeButton}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={handleAddField}
                                        className={classes.addButton}
                                    >
                                        Add Field
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResetSearchFields}
                                        className={classes.resetButton}
                                        style={{ marginLeft: "8px" }}
                                    >
                                        Reset
                                    </button>
                                </div>
                                <button type="submit" className={classes.saveButton}>
                                    Save
                                </button>
                                <button
                                    type="reset"
                                    className={classes.resetButton}
                                    onClick={handleResetConfig}
                                >
                                    Reset
                                </button>
                            </fieldset>
                        </form>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const HelpButton = ({ tooltip, docUrl, placement = "right" }) => (
    <OverlayTrigger
        placement={placement}
        overlay={<Tooltip>{tooltip}</Tooltip>}
    >
        <button
            type="button"
            className={classes.helpButton}
            tabIndex={0}
            onClick={(e) => {
                if (!docUrl) return;
                e.stopPropagation();
                window.open(docUrl, "_blank", "noopener,noreferrer");
            }}
            style={{
                background: "none",
                border: "none",
                color: "#007bff",
                cursor: "pointer",
                marginLeft: 4,
                fontSize: "1em",
                padding: 0,
            }}
            aria-label="Help"
        >
            (?)
        </button>
    </OverlayTrigger>
);

export default ElasticSearchConfigButton;
